import cn from 'classnames'
import { Icon } from './ui'
import { seatCount, TStep, useFirstRun } from './store'
import { FF_NUMBER, LEAD_AREAS, leadsFor, SEAT_EXTRA, SEAT_FIRST, STOP_QUESTIONS, ALL_STAGES_AND_TAGS } from './data'

interface IRow {
   id: string
   title: string
   icon: string
   steps: TStep[]
   value?: string
   sub?: string
}

/**
 * The left panel: one row per step, an icon in a tinted circle, the title,
 * and what was decided. Done rows show a green check and reopen on click;
 * the current row is highlighted. A row can cover two screens.
 */
export const SetupRail = () => {
   const s = useFirstRun()
   const order = s.steps()
   const seats = seatCount(s)
   const cost = SEAT_FIRST + (seats - 1) * SEAT_EXTRA
   const filled = Object.values(s.business).filter(Boolean).length
   const names = (ids: string[]) => ids.map((id) => ALL_STAGES_AND_TAGS.find((x) => x.id === id)?.name || id)
   const areaCity = (code: string) => LEAD_AREAS.find((a) => a.code === code)?.city

   const rows: IRow[] = ([
      { id: 'connect', title: 'Follow Up Boss', icon: 'key', steps: ['connect'], value: s.fubConnected ? 'Connected' : undefined, sub: s.fubConnected ? 'Patel Realty · 7 users' : undefined },
      { id: 'you', title: 'You', icon: 'user', steps: ['you'], value: s.owner.name, sub: s.owner.phone },
      { id: 'team', title: 'Your team', icon: 'users', steps: ['team'], value: seats === 1 ? 'Just you' : `You + ${seats - 1}`, sub: `$${cost}/mo` },
      { id: 'calendar', title: 'Calendar', icon: 'calendar', steps: ['calendar'], value: s.calendar ? (s.calendar === 'google' ? 'Google Calendar' : 'Outlook Calendar') : undefined },
      { id: 'budget', title: 'Budget', icon: 'gauge', steps: ['budget'], value: `$${s.budget}/mo`, sub: `about ${leadsFor(s.budget)} leads` },
      { id: 'payment', title: 'Payment', icon: 'card', steps: ['payment'], value: s.paid ? 'Card on file' : undefined },
      { id: 'area', title: 'Area code', icon: 'pin', steps: ['area'], value: s.areaCodes.map((c) => `(${c})`).join(', '), sub: areaCity(s.areaCodes[0]) },
      { id: 'number', title: 'Your number', icon: 'phone', steps: ['number'], value: FF_NUMBER },
      ...STOP_QUESTIONS.map((q) => ({ id: `stops-${q.id}`, title: q.title, icon: 'shield', steps: [`stops-${q.id}`] as TStep[], value: s.stops[q.id].length ? names(s.stops[q.id]).slice(0, 2).join(', ') + (s.stops[q.id].length > 2 ? ` +${s.stops[q.id].length - 2}` : '') : undefined })),
      { id: 'business', title: 'Texting approval', icon: 'file', steps: ['business'], value: s.businessSent ? 'Sent for approval' : filled ? `${filled} of 18 filled` : undefined },
   ] as IRow[]).filter((r) => r.steps.some((st) => order.includes(st)))

   const currentIndex = order.indexOf(s.step)
   return (
      <ol className="flex-1 flex flex-col py-2" data-testid="rail" aria-label="Setup">
         {rows.map((r) => {
            const idx = order.indexOf(r.steps[0])
            const state = r.steps.includes(s.step) ? 'current' : idx < currentIndex ? 'done' : 'todo'
            const inner = (
               <>
                  <span
                     className={cn(
                        'flex shrink-0 items-center justify-center w-9 h-9 rounded-full',
                        state === 'done' && 'bg-[var(--fr-good-tint)] text-[var(--fr-good)]',
                        state === 'current' && 'bg-[var(--fr-blue)] text-white',
                        state === 'todo' && 'bg-[var(--fr-surface-2)] text-[var(--fr-text-3)]'
                     )}
                  >
                     <Icon name={state === 'done' ? 'check' : r.icon} size={16} />
                  </span>
                  <span className="flex-1 min-w-0">
                     <span className={cn('block text-[14px] font-semibold truncate', state === 'todo' ? 'text-[var(--fr-text-3)]' : 'text-[var(--fr-text)]')}>{r.title}</span>
                     {state !== 'todo' && r.value ? (
                        <span className="block text-[12px] text-[var(--fr-text-2)] truncate">
                           {r.value}
                           {r.sub ? <span className="text-[var(--fr-text-3)]"> · {r.sub}</span> : null}
                        </span>
                     ) : state === 'current' ? (
                        <span className="block text-[12px] text-[var(--fr-blue)]">In progress</span>
                     ) : null}
                  </span>
                  {state === 'done' && <span className="text-[12px] font-semibold text-[var(--fr-blue)] opacity-0 group-hover:opacity-100">Edit</span>}
               </>
            )
            const cls = cn('group w-full flex items-center gap-3 text-left px-5 py-2.5 rounded-xl mx-2 transition-colors', state === 'current' && 'bg-[var(--fr-blue-tint)]', state === 'done' && 'hover:bg-[var(--fr-surface-2)] cursor-pointer')
            return (
               <li key={r.id} className="flex-1 min-h-0 flex items-center pr-4" data-testid={`rail-${r.id}`} data-state={state}>
                  {state === 'done' ? (
                     <button type="button" className={cls} onClick={() => s.go(r.steps[0])}>
                        {inner}
                     </button>
                  ) : (
                     <div className={cls} aria-current={state === 'current' ? 'step' : undefined}>
                        {inner}
                     </div>
                  )}
               </li>
            )
         })}
      </ol>
   )
}
